/** @format */

import {useEffect, useMemo, useRef, useState} from 'react';

import {Alert, Box, LinearProgress} from '@mui/material';
import {add, format, isValid} from 'date-fns';
import {useSearchParams} from 'react-router-dom';

import {Table} from 'components/Table';

import {useSearchQuery} from '../context';
import {useGetCurrentUserStaffQuery} from '../graphql/__generated__';
import {CompanyReportDownloadFromQuery} from '../types';
import {staffColumns} from './columns';
import {mapStaffData} from './mapData';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const Staff = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = useSearchQuery();
  const periodRange = searchParams.get('periodRange');

  const now = useRef(new Date()).current;

  const [startDate, setStartDate] = useState<Date>(() => add(now, {days: -31}));
  const [endDate, setEndDate] = useState<Date>(() => now);

  const {loading, data, error} = useGetCurrentUserStaffQuery({
    variables: {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    },
  });

  useEffect(() => {
    const ranges = periodRange?.split(', ');
    if (ranges && ranges.length >= 2) {
      const parsedStart = new Date(ranges[0]);
      const parsedEnd = new Date(ranges[1]);
      if (isValid(parsedStart) && isValid(parsedEnd)) {
        setStartDate(parsedStart);
        setEndDate(parsedEnd);
      }
    }
  }, [periodRange]);

  const amountOfWeeks = Math.round((endDate.getTime() - startDate.getTime()) / WEEK_MS);

  const reportDownloads = data?.currentUser?.companyReportDownloads;

  const tableData = useMemo(() => {
    const items = (reportDownloads ?? []).filter(
      (item): item is CompanyReportDownloadFromQuery => item != null,
    );
    return mapStaffData(items, {amountOfWeeks: amountOfWeeks || 1});
  }, [reportDownloads, amountOfWeeks]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }

  if (loading || !reportDownloads) {
    return (
      <Box sx={{width: '100%'}}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{mt: 4}}>
      <Table
        columns={staffColumns}
        data={tableData}
        search={searchQuery || undefined}
        searchByColumnIds={['staff']}
        defaultSortBy='downloads'
        defaultOrderBy='desc'
        variant='dark'
        columnResizingKey='chief-report-staff'
        stickyHeader
      />
    </Box>
  );
};

export default Staff;
